import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ModifiedTag from '@components/common/form/ModifiedField';
import { ScrollArea } from '@components/ui/ScrollArea';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { RoleSelectors, selectAllProfiles, selectProfileById } from '@redux/authent/authent.selectors';
import { ADMIN_PROFILE } from '@services/authent/authent.api';
import { useCreateProfileMutation, useUpdateProfileMutation } from '@services/authent/profile.api';
import { filterRoles, hasChangedCategoryRoles, isNotTechnical } from '@utils/authent/roles.utils';
import { haveSameUniqueElements } from '@utils/common.utils';
import { REQUIRED, UNIQUE } from '@utils/validation/validation.constant';
import Yup from '@utils/validation/yup-extended';
import { FormikProps, useFormik } from 'formik';
import { createContext, useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Profile } from '@/types/authent/groups.types';
import { minimumRolesEnum, RoleEnum, roleMap } from '@/types/authent/roles.types';

import { useUsersManagementContext } from '../UsersManagement';
import ProfileForm from './ProfileForm';
import RoleCategory from './RoleCategory';

export type ProfileFormType = Partial<Profile> & Pick<Profile, 'roles'>;

const ProfileFormContext = createContext<FormikProps<ProfileFormType> | undefined>(undefined);

export const useProfileFormContext = (): FormikProps<ProfileFormType> => {
  const context = useContext(ProfileFormContext);
  if (!context) {
    throw new Error('useProfileFormContext must be used within a ProfileFormContext.Provider');
  }
  return context;
};

const emptyProfile: ProfileFormType = {
  name: '',
  roles: minimumRolesEnum,
};

const missingRoles = Object.values(RoleEnum).filter(
  (role) =>
    !new Set(Object.values(roleMap).flatMap((group) => Object.values(group).flat())).has(role) && isNotTechnical(role),
);

type ProfileDialogProps = {
  isNewProfile: boolean;
  name: string | undefined;
} & Omit<CockpitDialogProps, 'children'>;

function ProfileDialog({
  isNewProfile,
  name,
  open,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<ProfileDialogProps>) {
  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.usersManagement.profiles.popup.${isNewProfile ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name: name }}
        />
      </Text>
    </CockpitDialog>
  );
}

export default function ProfileFormContainer() {
  const { formatMessage } = useIntl();

  const { editedItem, setEditedItem } = useUsersManagementContext();
  const profiles = useAppSelector(selectAllProfiles);
  const profile = useAppSelector((state) => selectProfileById(state, editedItem?.id ?? '')) ?? emptyProfile;
  const roles = useAppSelector(RoleSelectors.selectAllRoles);
  const allowedRolesMap = filterRoles(roles.map((role) => role.roleName));
  const [open, setOpen] = useState(false);
  const [createProfile, { isLoading: isLoadingCreate }] = useCreateProfileMutation();
  const [updateProfile, { isLoading: isLoadingUpdate }] = useUpdateProfileMutation();
  const isNewProfile = profile.id === undefined;
  const validationSchema = Yup.object({
    name: Yup.string()
      .required(formatMessage({ id: REQUIRED }))
      .notOneOf(
        profiles
          .map((profile) => profile.name)
          .concat(ADMIN_PROFILE)
          .filter((name) => name !== profile.name),
        formatMessage({ id: UNIQUE }),
      ),
  });
  const [tab, setTab] = useState<string | null>('settings');

  const formik = useFormik<ProfileFormType>({
    initialValues: profile,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isNewProfile) {
        createProfile(values as Profile)
          .unwrap()
          .then((profile) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.usersManagement.profiles.message.add' }, { name: profile.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      } else {
        updateProfile(values as Profile)
          .unwrap()
          .then((profile) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.usersManagement.profiles.message.update' }, { name: profile.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      }
    },
  });

  const contextValue = useMemo(() => formik, [formik]);

  return (
    <ProfileFormContext value={contextValue}>
      <CockpitEditorLayout
        dialog={
          <ProfileDialog
            open={open}
            isLoading={isLoadingCreate || isLoadingUpdate}
            confirmLabel={formatMessage({ id: 'global.save' })}
            cancelLabel={formatMessage({ id: 'global.cancel' })}
            onConfirm={formik.submitForm}
            onCancel={() => setOpen(false)}
            isNewProfile={isNewProfile}
            name={formik.values.name}
          />
        }
        onBack={() => setEditedItem(null)}
        onSave={() => setOpen(true)}
        isSaving={isLoadingCreate || isLoadingUpdate}
        isSaveDisabled={
          !formik.isValid ||
          (haveSameUniqueElements(formik.values.roles, profile.roles) && profile.name === formik.values.name)
        }
        breadcrumbs={
          <Breadcrumb
            items={[
              {
                label: formatMessage({ id: 'components.usersManagement.profiles.title' }),
                action: () => setEditedItem(null),
              },
              {
                label: isNewProfile
                  ? formatMessage({
                      id: 'components.usersManagement.profiles.newProfile',
                    })
                  : profile.name,
              },
            ]}
          />
        }
      >
        <Tabs.Root
          orientation="vertical"
          size="md"
          lazyMount
          variant="vertical"
          userSelect="none"
          value={tab}
          onValueChange={(e) => {
            if (e.value === 'roles') {
              setTab(Object.keys(allowedRolesMap)[0]);
            } else {
              setTab(e.value);
            }
          }}
        >
          <Tabs.List>
            <ScrollArea>
              <Tabs.Trigger value="settings">
                <HStack width="100%">
                  <FormattedMessage id="components.usersManagement.profiles.settings.title" />
                  {formik.initialValues.name !== formik.values.name && <ModifiedTag />}
                </HStack>
              </Tabs.Trigger>
              <Tabs.Trigger value="roles" aria-selected={tab !== 'settings'}>
                <HStack width="100%">
                  <FormattedMessage id="components.usersManagement.profiles.roles.title" />
                  {!haveSameUniqueElements(formik.initialValues.roles, formik.values.roles) && <ModifiedTag />}
                </HStack>
              </Tabs.Trigger>
              {Object.keys(allowedRolesMap).map((key) => (
                <Tabs.Trigger key={key} value={key} data-trigger-variant="secondary">
                  <HStack width="100%">
                    <FormattedMessage id={`components.usersManagement.profiles.roles.category.${key}`} />
                    {hasChangedCategoryRoles(formik.initialValues.roles, formik.values.roles, key) && <ModifiedTag />}
                  </HStack>
                </Tabs.Trigger>
              ))}
              {missingRoles.length > 0 ? (
                <Tabs.Trigger value="other" data-trigger-variant="secondary">
                  <FormattedMessage id={`components.usersManagement.profiles.roles.category.other`} />
                </Tabs.Trigger>
              ) : null}
            </ScrollArea>
          </Tabs.List>
          <Tabs.ContentGroup backgroundColor="neutral.700">
            <Tabs.Content value="settings">
              <ProfileForm />
            </Tabs.Content>
            {Object.entries(allowedRolesMap).map(([key, value]) => (
              <Tabs.Content key={key} value={key}>
                <RoleCategory name={key} category={value} />
              </Tabs.Content>
            ))}
            {missingRoles.length > 0 && (
              <Tabs.Content value="other">
                <RoleCategory name="other" category={{ other: missingRoles }} skipTranslation />
              </Tabs.Content>
            )}
          </Tabs.ContentGroup>
        </Tabs.Root>
      </CockpitEditorLayout>
    </ProfileFormContext>
  );
}
